import React, { useState, useEffect, useCallback, useRef, useMemo } from "react";
import axios from "axios";
import { CIVIL_DOC_KINDS, isCivilDocType } from "./civilDocs";
import { DocumentEditor } from "@onlyoffice/document-editor-react";
import {
  Upload,
  Download,
  Save,
  FileText,
  ChevronLeft,
  Code,
  Copy,
  Check,
  Plus,
  AlertTriangle,
  Info,
  X,
  Search,
  RefreshCw,
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "./AuthContext";

const API_BASE_URL =
  process.env.REACT_APP_API_BASE_URL !== undefined
    ? process.env.REACT_APP_API_BASE_URL
    : "http://localhost:5001";

const ONLYOFFICE_SERVER =
  process.env.REACT_APP_ONLYOFFICE_URL || "https://docs.ligalrus-recognizer.ru";

const Tooltip = ({ text, children }) => {
  const [show, setShow] = useState(false);
  return (
    <span
      style={{ position: "relative", display: "inline-flex" }}
      onMouseEnter={() => setShow(true)}
      onMouseLeave={() => setShow(false)}
    >
      {children}
      {show && (
        <span
          style={{
            position: "absolute",
            bottom: "calc(100% + 6px)",
            left: "50%",
            transform: "translateX(-50%)",
            background: "#1e293b",
            color: "#e2e8f0",
            padding: "6px 10px",
            borderRadius: "6px",
            fontSize: "0.75rem",
            whiteSpace: "nowrap",
            zIndex: 9999,
            border: "1px solid rgba(255,255,255,0.1)",
            pointerEvents: "none",
            boxShadow: "0 4px 12px rgba(0,0,0,0.4)",
          }}
        >
          {text}
        </span>
      )}
    </span>
  );
};

const StepBadge = ({ number, label, done, active }) => (
  <div
    style={{
      display: "flex",
      alignItems: "center",
      gap: "8px",
      padding: "6px 12px",
      borderRadius: "20px",
      background: done
        ? "rgba(16,185,129,0.15)"
        : active
          ? "rgba(59,130,246,0.15)"
          : "rgba(255,255,255,0.05)",
      border: `1px solid ${done ? "rgba(16,185,129,0.4)" : active ? "rgba(59,130,246,0.4)" : "rgba(255,255,255,0.1)"}`,
      fontSize: "0.8rem",
      color: done ? "#10b981" : active ? "#60a5fa" : "#9ca3af",
      fontWeight: 500,
      flexShrink: 0,
    }}
  >
    <span
      style={{
        width: "20px",
        height: "20px",
        borderRadius: "50%",
        background: done
          ? "#10b981"
          : active
            ? "#3b82f6"
            : "rgba(255,255,255,0.1)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        fontSize: "0.7rem",
        color: "white",
        fontWeight: 700,
        flexShrink: 0,
      }}
    >
      {done ? "✓" : number}
    </span>
    {label}
  </div>
);

/** UUID временного docx на диске бэкенда. Последний сегмент URL нельзя использовать при S3 presigned. */
function tempDocFileIdFromUrl(documentUrl) {
  if (!documentUrl || typeof documentUrl !== "string") return "";
  const m = documentUrl.match(/\/temp-doc\/([0-9a-f-]{36})/i);
  return m ? m[1] : "";
}

const TemplateEditor = () => {
  const navigate = useNavigate();
  const docEditorInstance = useRef(null);

  const [label, setLabel] = useState("");
  const [countryCode, setCountryCode] = useState("TJK");
  const [dateFormat, setDateFormat] = useState("DD.MM.YYYY");
  const [variables, setVariables] = useState({});
  const [selectedCountryForVars, setSelectedCountryForVars] = useState("");
  const [copiedVar, setCopiedVar] = useState(null);
  const [saving, setSaving] = useState(false);
  const [documentKey, setDocumentKey] = useState(`doc_${Date.now()}`);
  const [documentUrl, setDocumentUrl] = useState(null);
  const [editorTempFileId, setEditorTempFileId] = useState("");
  const [isDocumentReady, setIsDocumentReady] = useState(false);
  const [notification, setNotification] = useState(null);
  const [varSearch, setVarSearch] = useState("");

  const [showLoadFromTemplates, setShowLoadFromTemplates] = useState(false);
  const [existingTemplates, setExistingTemplates] = useState([]);
  const [loadingTemplates, setLoadingTemplates] = useState(false);
  const [templateSearch, setTemplateSearch] = useState("");
  const [editingTemplateId, setEditingTemplateId] = useState(null);
  const [documentType, setDocumentType] = useState("passport");

  const [validationErrors, setValidationErrors] = useState([]);
  const [showValidationModal, setShowValidationModal] = useState(false);
  const [isValidating, setIsValidating] = useState(false);

  const [showGuide, setShowGuide] = useState(() => {
    return localStorage.getItem("templateEditorGuideShown") !== "true";
  });

  const [useVisualMapping, setUseVisualMapping] = useState(true);
  const [visualMappingRows, setVisualMappingRows] = useState([]);
  const [docParagraphs, setDocParagraphs] = useState([]);
  const [paragraphsLoading, setParagraphsLoading] = useState(false);
  const [paragraphsOpen, setParagraphsOpen] = useState(false);
  const [hideLongParagraphs, setHideLongParagraphs] = useState(true);

  const { user } = useAuth();

  const effectiveTempFileId =
    editorTempFileId || tempDocFileIdFromUrl(documentUrl);

  const countries = [
    { code: "AZE", name: "Азербайджан" },
    { code: "ALB", name: "Албания" },
    { code: "DZA", name: "Алжир" },
    { code: "AGO", name: "Ангола" },
    { code: "AND", name: "Андорра" },
    { code: "ATG", name: "Антигуа и Барбуда" },
    { code: "USA", name: "США" },
    { code: "ARG", name: "Аргентина" },
    { code: "ARM", name: "Армения" },
    { code: "AUS", name: "Австралия" },
    { code: "AUT", name: "Австрия" },
    { code: "AFG", name: "Афганистан" },
    { code: "BHS", name: "Багамы" },
    { code: "BHR", name: "Бахрейн" },
    { code: "BGD", name: "Бангладеш" },
    { code: "BRB", name: "Барбадос" },
    { code: "BLR", name: "Беларусь" },
    { code: "BLZ", name: "Белиз" },
    { code: "BEL", name: "Бельгия" },
    { code: "BEN", name: "Бенин" },
    { code: "BGR", name: "Болгария" },
    { code: "BOL", name: "Боливия" },
    { code: "BIH", name: "Босния и Герцеговина" },
    { code: "BWA", name: "Ботсвана" },
    { code: "BRA", name: "Бразилия" },
    { code: "BRN", name: "Бруней" },
    { code: "BFA", name: "Буркина-Фасо" },
    { code: "BDI", name: "Бурунди" },
    { code: "BTN", name: "Бутан" },
    { code: "VUT", name: "Вануату" },
    { code: "VAT", name: "Ватикан" },
    { code: "GBR", name: "Великобритания" },
    { code: "HUN", name: "Венгрия" },
    { code: "VEN", name: "Венесуэла" },
    { code: "VNM", name: "Вьетнам" },
    { code: "GAB", name: "Габон" },
    { code: "HTI", name: "Гаити" },
    { code: "GUY", name: "Гайана" },
    { code: "GMB", name: "Гамбия" },
    { code: "GHA", name: "Гана" },
    { code: "GTM", name: "Гватемала" },
    { code: "GIN", name: "Гвинея" },
    { code: "GNB", name: "Гвинея-Бисау" },
    { code: "DEU", name: "Германия" },
    { code: "HND", name: "Гондурас" },
    { code: "GRD", name: "Гренада" },
    { code: "GEO", name: "Грузия" },
    { code: "GRC", name: "Греция" },
    { code: "DNK", name: "Дания" },
    { code: "DJI", name: "Джибути" },
    { code: "DMA", name: "Доминика" },
    { code: "DOM", name: "Доминиканская Республика" },
    { code: "COD", name: "Демократическая Республика Конго" },
    { code: "EGY", name: "Египет" },
    { code: "ZMB", name: "Замбия" },
    { code: "ZWE", name: "Зимбабве" },
    { code: "ISR", name: "Израиль" },
    { code: "IND", name: "Индия" },
    { code: "IDN", name: "Индонезия" },
    { code: "JOR", name: "Иордания" },
    { code: "IRQ", name: "Ирак" },
    { code: "IRN", name: "Иран" },
    { code: "IRL", name: "Ирландия" },
    { code: "ISL", name: "Исландия" },
    { code: "ESP", name: "Испания" },
    { code: "ITA", name: "Италия" },
    { code: "YEM", name: "Йемен" },
    { code: "CPV", name: "Кабо-Верде" },
    { code: "KAZ", name: "Казахстан" },
    { code: "KHM", name: "Камбоджа" },
    { code: "CMR", name: "Камерун" },
    { code: "CAN", name: "Канада" },
    { code: "QAT", name: "Катар" },
    { code: "KEN", name: "Кения" },
    { code: "CYP", name: "Кипр" },
    { code: "KGZ", name: "Кыргызстан" },
    { code: "KIR", name: "Кирибати" },
    { code: "CHN", name: "Китай" },
    { code: "COL", name: "Колумбия" },
    { code: "COM", name: "Коморы" },
    { code: "COG", name: "Республика Конго" },
    { code: "CRI", name: "Коста-Рика" },
    { code: "CIV", name: "Кот-д'Ивуар" },
    { code: "CUB", name: "Куба" },
    { code: "KWT", name: "Кувейт" },
    { code: "PRK", name: "Северная Корея" },
    { code: "KOR", name: "Южная Корея" },
    { code: "LAO", name: "Лаос" },
    { code: "LVA", name: "Латвия" },
    { code: "LSO", name: "Лесото" },
    { code: "LBR", name: "Либерия" },
    { code: "LBN", name: "Ливан" },
    { code: "LBY", name: "Ливия" },
    { code: "LTU", name: "Литва" },
    { code: "LIE", name: "Лихтенштейн" },
    { code: "LUX", name: "Люксембург" },
    { code: "MUS", name: "Маврикий" },
    { code: "MRT", name: "Мавритания" },
    { code: "MDG", name: "Мадагаскар" },
    { code: "MKD", name: "Северная Македония" },
    { code: "MWI", name: "Малави" },
    { code: "MYS", name: "Малайзия" },
    { code: "MLI", name: "Мали" },
    { code: "MDV", name: "Мальдивы" },
    { code: "MLT", name: "Мальта" },
    { code: "MAR", name: "Марокко" },
    { code: "MHL", name: "Маршалловы Острова" },
    { code: "MEX", name: "Мексика" },
    { code: "FSM", name: "Микронезия" },
    { code: "MOZ", name: "Мозамбик" },
    { code: "MDA", name: "Молдова" },
    { code: "MCO", name: "Монако" },
    { code: "MNG", name: "Монголия" },
    { code: "MMR", name: "Мьянма" },
    { code: "NAM", name: "Намибия" },
    { code: "NRU", name: "Науру" },
    { code: "NPL", name: "Непал" },
    { code: "NER", name: "Нигер" },
    { code: "NGA", name: "Нигерия" },
    { code: "NLD", name: "Нидерланды" },
    { code: "NIC", name: "Никарагуа" },
    { code: "NZL", name: "Новая Зеландия" },
    { code: "NOR", name: "Норвегия" },
    { code: "ARE", name: "ОАЭ" },
    { code: "OMN", name: "Оман" },
    { code: "PAK", name: "Пакистан" },
    { code: "PLW", name: "Палау" },
    { code: "PSE", name: "Палестина" },
    { code: "PAN", name: "Панама" },
    { code: "PNG", name: "Папуа — Новая Гвинея" },
    { code: "PRY", name: "Парагвай" },
    { code: "PER", name: "Перу" },
    { code: "POL", name: "Польша" },
    { code: "PRT", name: "Португалия" },
    { code: "RUS", name: "Россия" },
    { code: "RWA", name: "Руанда" },
    { code: "ROU", name: "Румыния" },
    { code: "SLV", name: "Сальвадор" },
    { code: "WSM", name: "Самоа" },
    { code: "SMR", name: "Сан-Марино" },
    { code: "STP", name: "Сан-Томе и Принсипи" },
    { code: "SAU", name: "Саудовская Аравия" },
    { code: "SWZ", name: "Эсватини" },
    { code: "SYC", name: "Сейшелы" },
    { code: "SEN", name: "Сенегал" },
    { code: "VCT", name: "Сент-Винсент и Гренадины" },
    { code: "KNA", name: "Сент-Китс и Невис" },
    { code: "LCA", name: "Сент-Люсия" },
    { code: "SRB", name: "Сербия" },
    { code: "SGP", name: "Сингапур" },
    { code: "SYR", name: "Сирия" },
    { code: "SVK", name: "Словакия" },
    { code: "SVN", name: "Словения" },
    { code: "SLB", name: "Соломоновы Острова" },
    { code: "SOM", name: "Сомали" },
    { code: "SDN", name: "Судан" },
    { code: "SUR", name: "Суринам" },
    { code: "SLE", name: "Сьерра-Леоне" },
    { code: "TJK", name: "Таджикистан" },
    { code: "THA", name: "Таиланд" },
    { code: "TZA", name: "Танзания" },
    { code: "TGO", name: "Того" },
    { code: "TON", name: "Тонга" },
    { code: "TTO", name: "Тринидад и Тобаго" },
    { code: "TUV", name: "Тувалу" },
    { code: "TUN", name: "Тунис" },
    { code: "TKM", name: "Туркменистан" },
    { code: "TUR", name: "Турция" },
    { code: "UGA", name: "Уганда" },
    { code: "UZB", name: "Узбекистан" },
    { code: "UKR", name: "Украина" },
    { code: "URY", name: "Уругвай" },
    { code: "FJI", name: "Фиджи" },
    { code: "PHL", name: "Филиппины" },
    { code: "FIN", name: "Финляндия" },
    { code: "FRA", name: "Франция" },
    { code: "HRV", name: "Хорватия" },
    { code: "CAF", name: "Центральноафриканская Республика" },
    { code: "TCD", name: "Чад" },
    { code: "MNE", name: "Черногория" },
    { code: "CZE", name: "Чехия" },
    { code: "CHL", name: "Чили" },
    { code: "CHE", name: "Швейцария" },
    { code: "SWE", name: "Швеция" },
    { code: "LKA", name: "Шри-Ланка" },
    { code: "ECU", name: "Эквадор" },
    { code: "GNQ", name: "Экваториальная Гвинея" },
    { code: "ERI", name: "Эритрея" },
    { code: "EST", name: "Эстония" },
    { code: "ETH", name: "Эфиопия" },
    { code: "ZAF", name: "ЮАР" },
    { code: "SSD", name: "Южный Судан" },
    { code: "JAM", name: "Ямайка" },
    { code: "JPN", name: "Япония" },
  ].sort((a, b) => a.name.localeCompare(b.name, "ru"));

  const allKnownVarKeys = useCallback(() => {
    const keys = new Set();
    Object.values(variables).forEach((cat) => {
      cat.fields?.forEach((f) => {
        keys.add(f.key);
        f.parts?.forEach((p) => keys.add(p.key));
      });
    });

    [
      "03",
      "04",
      "05",
      "06",
      "07",
      "08",
      "09",
      "10",
      "11",
      "12",
      "13",
      "14",
      "15",
      "16",
      "17",
      "18",
      "19",
      "20",
      "21",
      "22",
      "23",
      "24",
      "25",
      "26",
      "27",
      "28",
      "29",
      "30",
      "31",
      "32",
      "33",
      "34",
      "35",
      "36",
      "37",
      "38",
      "39",
      "40",
      "41",
      "42",
      "43",
      "44",
      "45",
      "46",
    ].forEach((n) => keys.add(`page${n}`));
    return keys;
  }, [variables]);

  const validateDocumentVariables = useCallback(async () => {
    if (!documentUrl || !isDocumentReady) return true;

    setIsValidating(true);
    try {
      const fileId = effectiveTempFileId;
      if (!fileId) return true;

      try {
        await axios.post(
          `${API_BASE_URL}/api/templates/force-save-and-wait/${fileId}`,
          { key: documentKey },
        );
      } catch (_) {}

      const response = await axios.get(
        `${API_BASE_URL}/api/templates/extract-text/${fileId}`,
        { responseType: "json" },
      );

      const text = response.data?.text || "";
      return validateText(text);
    } catch (err) {
      console.warn("Validation via API failed, skipping:", err);

      return { errors: [], warnings: [] };
    } finally {
      setIsValidating(false);
    }
  }, [documentUrl, documentKey, isDocumentReady, effectiveTempFileId]);

  const validateText = useCallback(
    (text) => {
      const errors = [];
      const warnings = [];
      const known = allKnownVarKeys();

      let i = 0;
      const openStack = [];

      while (i < text.length) {
        if (text[i] === "{" && text[i + 1] === "{") {
          openStack.push(i);
          i += 2;
          continue;
        }

        if (text[i] === "}" && text[i + 1] === "}") {
          if (openStack.length === 0) {
            const snippet = text
              .slice(Math.max(0, i - 20), i + 2)
              .replace(/\n/g, " ")
              .trim();
            errors.push({
              type: "unopened",
              message: `Лишние }} без открывающего {{ : «...${snippet}»`,
              hint: "Удалите лишние }} или добавьте {{ перед ними",
            });
          } else {
            const openIdx = openStack.pop();

            const inner = text.slice(openIdx + 2, i);
            if (inner.includes("{{")) {
              const snippet = text
                .slice(openIdx, i + 2)
                .replace(/\n/g, " ")
                .trim()
                .slice(0, 60);
              errors.push({
                type: "nested",
                message: `Вложенные скобки: «${snippet}»`,
                hint: "Переменные не могут быть вложенными",
              });
            }
          }
          i += 2;
          continue;
        }

        i++;
      }

      openStack.forEach((openIdx) => {
        const snippet = text
          .slice(openIdx, openIdx + 40)
          .replace(/\n/g, " ")
          .trim();
        errors.push({
          type: "unclosed",
          message: `Незакрытая переменная: «${snippet}...»`,
          hint: "Добавьте }} в конце переменной",
        });
      });

      const varRegex = /\{\{\s*([\w.]+)(?:\|[\w|]+)?\s*\}\}/g;
      let m;
      const foundVars = new Set();

      while ((m = varRegex.exec(text)) !== null) {
        foundVars.add(m[1]);
      }

      foundVars.forEach((varKey) => {
        if (!known.has(varKey)) {
          warnings.push({
            type: "unknown",
            message: `Неизвестная переменная: {{ ${varKey} }}`,
            hint: "Проверьте название переменной — возможно, опечатка",
          });
        }
      });

      const lineByLine = text.split("\n");
      lineByLine.forEach((line, lineIdx) => {
        const opens = (line.match(/\{\{/g) || []).length;
        const closes = (line.match(/\}\}/g) || []).length;

        if (opens > closes) {
          const trimmed = line.trim().slice(0, 60);

          const alreadyReported = errors.some((e) =>
            e.message.includes(trimmed.slice(0, 20)),
          );
          if (!alreadyReported && trimmed.length > 0) {
            errors.push({
              type: "unclosed_line",
              message: `Незакрытая переменная в строке ${lineIdx + 1}: «${trimmed}»`,
              hint: "Добавьте }} в конце переменной",
            });
          }
        }

        if (closes > opens) {
          const trimmed = line.trim().slice(0, 60);
          const alreadyReported = errors.some((e) =>
            e.message.includes(trimmed.slice(0, 20)),
          );
          if (!alreadyReported && trimmed.length > 0) {
            errors.push({
              type: "extra_close_line",
              message: `Лишние }} в строке ${lineIdx + 1}: «${trimmed}»`,
              hint: "Удалите лишние }} или добавьте {{ перед ними",
            });
          }
        }
      });

      return { errors, warnings };
    },
    [allKnownVarKeys],
  );

  const fetchExistingTemplates = useCallback(async () => {
    try {
      setLoadingTemplates(true);
      const [userResp, publicResp] = await Promise.all([
        axios.get(`${API_BASE_URL}/api/user-templates`),
        axios.get(`${API_BASE_URL}/api/user-templates/public`),
      ]);

      const userTpls = (userResp.data || []).map((t) => ({
        ...t,
        own: true,
        is_public: t.is_public || false,
      }));

      const publicTpls = (publicResp.data || [])
        .filter((t) => !userTpls.find((u) => u.id === t.id))
        .map((t) => ({ ...t, own: false }));

      const all = user?.is_admin ? [...userTpls, ...publicTpls] : userTpls;
      setExistingTemplates(all);
    } catch (err) {
      console.error("Failed to load templates", err);
    } finally {
      setLoadingTemplates(false);
    }
  }, [user]);

  const addVisualRow = () => {
    setVisualMappingRows((prev) => [
      ...prev,
      {
        id: `vm_${Date.now()}_${Math.random().toString(36).slice(2, 9)}`,
        template_key: "",
        literal: "",
      },
    ]);
  };

  const removeVisualRow = (id) => {
    setVisualMappingRows((prev) => prev.filter((r) => r.id !== id));
  };

  const updateVisualRow = (id, patch) => {
    setVisualMappingRows((prev) =>
      prev.map((r) => (r.id === id ? { ...r, ...patch } : r)),
    );
  };

  const fetchDocParagraphs = async () => {
    if (!documentUrl) return;
    const fileId = effectiveTempFileId;
    if (!fileId) {
      alert(
        "Неизвестен id временного файла. Перезагрузите страницу или снова загрузите документ.",
      );
      return;
    }
    setParagraphsLoading(true);
    try {
      try {
        await axios.post(
          `${API_BASE_URL}/api/templates/force-save-and-wait/${fileId}`,
          { key: documentKey },
        );
      } catch (_) {}
      const r = await axios.get(
        `${API_BASE_URL}/api/templates/doc-paragraphs/${fileId}`,
      );
      setDocParagraphs(r.data?.paragraphs || []);
      setParagraphsOpen(true);
    } catch (err) {
      console.error(err);
      alert(
        "Не удалось загрузить текст документа: " +
          (err.response?.data?.error || err.message),
      );
    } finally {
      setParagraphsLoading(false);
    }
  };

  const handleLoadFromTemplate = async (template) => {
    try {
      setNotification({
        text: "Загрузка шаблона...",
        variable: "",
        isWaitingPaste: false,
        type: "info",
      });

      const response = await axios.get(
        `${API_BASE_URL}/api/user-templates/${template.id}/download`,
        { responseType: "blob" },
      );

      const file = new File([response.data], `${template.label}.docx`, {
        type: "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
      });

      const formData = new FormData();
      formData.append("file", file);

      const uploadResp = await axios.post(
        `${API_BASE_URL}/api/templates/upload-temp-doc`,
        formData,
        { headers: { "Content-Type": "multipart/form-data" } },
      );

      setDocumentUrl(uploadResp.data.url);
      setEditorTempFileId(uploadResp.data.file_id || "");
      setDocumentKey(`doc_${Date.now()}`);
      setCountryCode(template.country_code || "TJK");
      setDateFormat(template.date_format || "DD.MM.YYYY");
      setDocumentType(template.document_type || "passport");
      setEditingTemplateId(template.id);
      setShowLoadFromTemplates(false);
      setTemplateSearch("");
      setDocParagraphs([]);
      setParagraphsOpen(false);

      const vm = template.visual_mappings;
      if (Array.isArray(vm) && vm.length > 0) {
        setUseVisualMapping(true);
        setVisualMappingRows(
          vm.map((m, i) => ({
            id: `vm_${template.id}_${i}`,
            template_key: m.template_key || m.templateKey || "",
            literal: m.literal || "",
          })),
        );
      } else {
        setUseVisualMapping(false);
        setVisualMappingRows([]);
      }

      setNotification({
        text: `✅ Загружен: ${template.label}`,
        variable: "",
        isWaitingPaste: false,
        type: "success",
      });
      setTimeout(() => setNotification(null), 2000);
    } catch (err) {
      console.error("Failed to load template", err);
      setNotification(null);
      alert(
        "Ошибка загрузки шаблона: " +
          (err.response?.data?.error || err.message),
      );
    }
  };

  const fetchVariables = useCallback(async (country, docType) => {
    try {
      const qs = new URLSearchParams();
      if (country) qs.set("country", country);
      // для документов РФ — переменные из реестра типов, а не паспортные
      if (isCivilDocType(docType)) qs.set("document_type", docType);
      const params = qs.toString() ? `?${qs}` : "";
      const response = await axios.get(
        `${API_BASE_URL}/api/templates/template-variables${params}`,
      );
      setVariables(response.data.categories);
    } catch (err) {
      console.error("Failed to load variables", err);
      setVariables({});
    }
  }, []);

  useEffect(() => {
    fetchVariables(selectedCountryForVars, documentType);
  }, [fetchVariables, selectedCountryForVars, documentType]);

  useEffect(() => {
    createEmptyDocument();
  }, []);

  const createEmptyDocument = async () => {
    try {
      const response = await axios.post(
        `${API_BASE_URL}/api/templates/create-empty-doc`,
      );
      setDocumentUrl(response.data.url);
      setEditorTempFileId(response.data.file_id || "");
      setDocumentKey(`doc_${Date.now()}`);
      setVisualMappingRows([]);
      setDocParagraphs([]);
      setParagraphsOpen(false);
    } catch (err) {
    }
  };

  const handleLoadDocx = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    try {
      const formData = new FormData();
      formData.append("file", file);

      const response = await axios.post(
        `${API_BASE_URL}/api/templates/upload-temp-doc`,
        formData,
        { headers: { "Content-Type": "multipart/form-data" } },
      );

      setDocumentUrl(response.data.url);
      setEditorTempFileId(response.data.file_id || "");
      setDocumentKey(`doc_${Date.now()}`);
      setIsDocumentReady(false);
      setVisualMappingRows([]);
      setDocParagraphs([]);
      setParagraphsOpen(false);
    } catch (err) {
      alert("Ошибка загрузки файла: " + err.message);
    }
  };

  const handleDownloadDocx = async () => {
    try {
      setNotification({
        text: "Сохранение изменений...",
        variable: "",
        isWaitingPaste: false,
        type: "info",
      });

      const fileId = effectiveTempFileId;
      if (!fileId) {
        setNotification(null);
        alert("Неизвестен id временного файла. Перезагрузите страницу.");
        return;
      }

      try {
        await axios.post(
          `${API_BASE_URL}/api/templates/force-save-and-wait/${fileId}`,
          { key: documentKey },
        );
      } catch (saveError) {
        console.warn("Force save failed:", saveError);
      }

      setNotification({
        text: "Скачивание файла...",
        variable: "",
        isWaitingPaste: false,
        type: "info",
      });

      const response = await axios.get(
        `${API_BASE_URL}/api/templates/download-current/${fileId}`,
        { responseType: "blob" },
      );

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      const filename = label ? `${label}.docx` : "template.docx";
      link.setAttribute("download", filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      setNotification({
        text: "✅ Файл успешно скачан",
        variable: "",
        isWaitingPaste: false,
        type: "success",
      });
      setTimeout(() => setNotification(null), 2000);
    } catch (error) {
      console.error("Download error:", error);
      setNotification(null);
      alert("Ошибка скачивания: " + error.message);
    }
  };

  const handleSaveAsTemplate = async () => {
    if (!label.trim()) {
      alert("Укажите название шаблона");
      return;
    }

    const fileId = effectiveTempFileId;
    if (!fileId) {
      alert(
        "Внутренний id документа не найден. Обновите страницу и откройте редактор снова.",
      );
      return;
    }
    try {
      await axios.post(
        `${API_BASE_URL}/api/templates/force-save-and-wait/${fileId}`,
        { key: documentKey },
      );
    } catch (_) {}

    if (useVisualMapping) {
      const visuals = visualMappingRows
        .filter((r) => r.template_key && r.literal.trim())
        .map((r) => ({
          template_key: r.template_key.trim(),
          literal: r.literal.trim(),
        }));

      if (visuals.length === 0) {
        alert(
          "В режиме «Разметка списком» добавьте хотя бы одну строку: поле данных и точный фрагмент текста из документа. Либо переключитесь на режим «Вручную {{ }}».",
        );
        return;
      }

      const incomplete = visualMappingRows.some(
        (r) =>
          (r.template_key && !r.literal.trim()) ||
          (r.literal.trim() && !r.template_key),
      );
      if (incomplete) {
        alert(
          "В каждой строке заполните и поле данных, и фрагмент из документа.",
        );
        return;
      }

      await doSave();
      return;
    }

    setIsValidating(true);
    let validationResult = { errors: [], warnings: [] };
    try {
      const resp = await axios.get(
        `${API_BASE_URL}/api/templates/extract-text/${fileId}`,
        { responseType: "json" },
      );
      validationResult = validateText(resp.data?.text || "");
    } catch (err) {
      console.warn("Could not validate, proceeding:", err);
    } finally {
      setIsValidating(false);
    }

    if (validationResult.errors.length > 0) {
      setValidationErrors(validationResult);
      setShowValidationModal(true);
      return;
    }

    if (validationResult.warnings.length > 0) {
      setValidationErrors(validationResult);
      setShowValidationModal(true);
      return;
    }

    await doSave();
  };

  const doSave = async () => {
    setShowValidationModal(false);
    try {
      setSaving(true);
      const fileId = effectiveTempFileId;
      if (!fileId) {
        alert(
          "Внутренний id документа не найден. Обновите страницу и откройте редактор снова.",
        );
        setSaving(false);
        return;
      }

      const visualsPayload = useVisualMapping
        ? visualMappingRows
            .filter((r) => r.template_key && r.literal.trim())
            .map((r) => ({
              template_key: r.template_key.trim(),
              literal: r.literal.trim(),
            }))
        : [];

      await axios.post(
        `${API_BASE_URL}/api/templates/save-template-from-editor`,
        {
          file_id: fileId,
          document_key: documentKey,
          country_code: countryCode,
          label: label,
          date_format: dateFormat,
          document_type: documentType,
          visual_mappings: useVisualMapping ? visualsPayload : [],
          ...(editingTemplateId
            ? { update_template_id: editingTemplateId }
            : {}),
        },
      );

      alert(
        editingTemplateId
          ? "✓ Шаблон успешно обновлён!"
          : "✓ Шаблон успешно сохранён!",
      );
      navigate("/templates");
    } catch (err) {
      const data = err.response?.data;
      const missing = data?.missing_literals;
      let msg = data?.error || err.message;
      if (missing?.length) {
        msg += "\n\nНе найдено в документе: " + missing.join(", ");
      }
      alert("Ошибка сохранения: " + msg);
    } finally {
      setSaving(false);
    }
  };

  const handleInsertVariable = async (varCode) => {
    const text = `{{ ${varCode} }}`;
    try {
      await navigator.clipboard.writeText(text);
      const iframe = document.querySelector("#onlyoffice-editor iframe");
      if (iframe) {
        iframe.focus();
        iframe.contentWindow?.focus();
      }
      setNotification({
        text: `Нажмите Ctrl+V для вставки`,
        variable: text,
        isWaitingPaste: true,
        type: "paste",
      });
      const handleKeyDown = (e) => {
        if ((e.ctrlKey || e.metaKey) && e.key === "v") {
          setNotification({
            text: `✅ Вставлено: ${text}`,
            variable: text,
            type: "success",
          });
          setTimeout(() => setNotification(null), 1500);
          document.removeEventListener("keydown", handleKeyDown);
        }
      };
      document.addEventListener("keydown", handleKeyDown);
      setTimeout(() => {
        setNotification(null);
        document.removeEventListener("keydown", handleKeyDown);
      }, 5000);
    } catch (error) {
      console.error("Failed:", error);
    }
  };

  const handleCopyVar = (varCode) => {
    navigator.clipboard.writeText(`{{ ${varCode} }}`);
    setCopiedVar(varCode);
    setTimeout(() => setCopiedVar(null), 2000);
  };

  const onDocumentReady = () => {
    setIsDocumentReady(true);
    if (window.DocEditor?.instances) {
      docEditorInstance.current =
        window.DocEditor.instances["onlyoffice-editor"];
    }
  };

  // Конфиг редактора собирает и подписывает (JWT) сервер — браузер секрета
  // не знает, поэтому URL документа и callbackUrl здесь не формируются.
  const [config, setConfig] = useState(null);
  useEffect(() => {
    const fileId = effectiveTempFileId;
    if (!documentUrl || !fileId) {
      setConfig(null);
      return;
    }
    let cancelled = false;
    axios
      .post(`${API_BASE_URL}/api/templates/editor-config`, {
        file_id: fileId,
        document_key: documentKey,
      })
      .then((r) => {
        if (!cancelled) setConfig({ ...r.data, height: "100%", width: "100%" });
      })
      .catch((e) => {
        console.error("Failed to load editor config:", e);
        if (!cancelled) setConfig(null);
      });
    return () => {
      cancelled = true;
    };
  }, [documentUrl, documentKey, effectiveTempFileId]);

  const DL_CATS = ["dl_main", "dl_categories"];
  const PASSPORT_HIDE = ["visas", "stamps", "registration_stamps", ...DL_CATS];

  const filteredVariables = Object.entries(variables).filter(
    ([categoryKey]) => {
      if (documentType === "drivers_license")
        return DL_CATS.includes(categoryKey);
      return !PASSPORT_HIDE.includes(categoryKey);
    },
  );

  const filteredVarsWithSearch = varSearch.trim()
    ? filteredVariables
        .map(([catKey, cat]) => {
          const matchedFields = cat.fields.filter(
            (f) =>
              f.key.toLowerCase().includes(varSearch.toLowerCase()) ||
              f.label?.toLowerCase().includes(varSearch.toLowerCase()),
          );
          return matchedFields.length
            ? [catKey, { ...cat, fields: matchedFields }]
            : null;
        })
        .filter(Boolean)
    : filteredVariables;

  const flatFieldSelectOptions = useMemo(() => {
    const opts = [];
    for (const [, cat] of filteredVariables) {
      const catLabel = cat.name || "";
      for (const f of cat.fields || []) {
        opts.push({
          key: f.key,
          label: f.label || f.key,
          sub: catLabel,
        });
        if (f.has_parts && f.parts) {
          for (const p of f.parts) {
            opts.push({
              key: p.key,
              label: `${f.label || f.key} — ${p.label}`,
              sub: catLabel,
            });
          }
        }
      }
    }
    return opts;
  }, [filteredVariables]);

  const passportPageVarKeys = useMemo(() => {
    const nums = [
      "03", "04", "05", "06", "07", "08", "09", "10", "11", "12", "13", "14",
      "15", "16", "17", "18", "19", "20", "21", "22", "23", "24", "25", "26",
      "27", "28", "29", "30", "31", "32", "33", "34", "35", "36", "37", "38",
      "39", "40", "41", "42", "43", "44", "45", "46",
    ];
    return nums.map((n) => `page${n}`);
  }, []);

  const visibleDocParagraphs = useMemo(() => {
    if (!hideLongParagraphs) return docParagraphs;
    return docParagraphs.filter((p) => (p.text || "").length <= 120);
  }, [docParagraphs, hideLongParagraphs]);

  const step1Done = isDocumentReady;
  const step2Done = isDocumentReady;
  const step3Done = !!label.trim();
  const canManualPaste = isDocumentReady && !useVisualMapping;

  const ValidationModal = () => {
    const hasErrors = validationErrors.errors?.length > 0;
    const hasWarnings = validationErrors.warnings?.length > 0;

    return (
      <div
        style={{
          position: "fixed",
          inset: 0,
          background: "rgba(0,0,0,0.7)",
          zIndex: 10000,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          padding: "1rem",
        }}
        onClick={(e) =>
          e.target === e.currentTarget && setShowValidationModal(false)
        }
      >
        <div
          style={{
            background: "var(--card)",
            borderRadius: "16px",
            border: hasErrors
              ? "1px solid rgba(239,68,68,0.5)"
              : "1px solid rgba(251,191,36,0.5)",
            maxWidth: "540px",
            width: "100%",
            overflow: "hidden",
            boxShadow: "0 24px 64px rgba(0,0,0,0.6)",
          }}
        >
          <div
            style={{
              padding: "1.25rem 1.5rem",
              background: hasErrors
                ? "rgba(239,68,68,0.1)"
                : "rgba(251,191,36,0.1)",
              borderBottom: hasErrors
                ? "1px solid rgba(239,68,68,0.3)"
                : "1px solid rgba(251,191,36,0.3)",
              display: "flex",
              alignItems: "center",
              gap: "12px",
            }}
          >
            <AlertTriangle
              size={22}
              color={hasErrors ? "#ef4444" : "#f59e0b"}
            />
            <div style={{ flex: 1 }}>
              <div
                style={{
                  fontWeight: 700,
                  fontSize: "1rem",
                  color: hasErrors ? "#ef4444" : "#f59e0b",
                }}
              >
                {hasErrors ? "Найдены ошибки в переменных" : "Предупреждения"}
              </div>
              <div
                style={{
                  fontSize: "0.8rem",
                  color: "#9ca3af",
                  marginTop: "2px",
                }}
              >
                {hasErrors
                  ? "Исправьте ошибки перед сохранением"
                  : "Обнаружены предупреждения — вы можете продолжить"}
              </div>
            </div>
            <button
              onClick={() => setShowValidationModal(false)}
              style={{
                background: "transparent",
                border: "none",
                color: "#9ca3af",
                cursor: "pointer",
                padding: "4px",
              }}
            >
              <X size={18} />
            </button>
          </div>

          <div
            style={{
              padding: "1.25rem 1.5rem",
              maxHeight: "360px",
              overflowY: "auto",
            }}
          >
            {hasErrors && (
              <div style={{ marginBottom: "1rem" }}>
                <div
                  style={{
                    fontSize: "0.75rem",
                    textTransform: "uppercase",
                    color: "#ef4444",
                    fontWeight: 700,
                    marginBottom: "0.5rem",
                    letterSpacing: "0.5px",
                  }}
                >
                  Ошибки ({validationErrors.errors.length})
                </div>
                {validationErrors.errors.map((err, i) => (
                  <div
                    key={i}
                    style={{
                      padding: "10px 14px",
                      background: "rgba(239,68,68,0.08)",
                      border: "1px solid rgba(239,68,68,0.25)",
                      borderRadius: "8px",
                      marginBottom: "8px",
                    }}
                  >
                    <div
                      style={{
                        fontSize: "0.875rem",
                        color: "#fca5a5",
                        fontWeight: 500,
                      }}
                    >
                      {err.message}
                    </div>
                    <div
                      style={{
                        fontSize: "0.75rem",
                        color: "#9ca3af",
                        marginTop: "4px",
                      }}
                    >
                      💡 {err.hint}
                    </div>
                  </div>
                ))}
              </div>
            )}

            {hasWarnings && (
              <div>
                <div
                  style={{
                    fontSize: "0.75rem",
                    textTransform: "uppercase",
                    color: "#f59e0b",
                    fontWeight: 700,
                    marginBottom: "0.5rem",
                    letterSpacing: "0.5px",
                  }}
                >
                  Предупреждения ({validationErrors.warnings.length})
                </div>
                {validationErrors.warnings.map((w, i) => (
                  <div
                    key={i}
                    style={{
                      padding: "10px 14px",
                      background: "rgba(251,191,36,0.08)",
                      border: "1px solid rgba(251,191,36,0.25)",
                      borderRadius: "8px",
                      marginBottom: "8px",
                    }}
                  >
                    <div
                      style={{
                        fontSize: "0.875rem",
                        color: "#fde68a",
                        fontWeight: 500,
                      }}
                    >
                      {w.message}
                    </div>
                    <div
                      style={{
                        fontSize: "0.75rem",
                        color: "#9ca3af",
                        marginTop: "4px",
                      }}
                    >
                      💡 {w.hint}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div
            style={{
              padding: "1rem 1.5rem",
              borderTop: "1px solid var(--border)",
              display: "flex",
              gap: "0.75rem",
              justifyContent: "flex-end",
            }}
          >
            <button
              onClick={() => setShowValidationModal(false)}
              className="secondary-btn"
              style={{ padding: "8px 16px" }}
            >
              Исправить
            </button>
            {!hasErrors && (
              <button
                onClick={doSave}
                className="primary-btn"
                style={{ padding: "8px 16px" }}
              >
                Сохранить всё равно
              </button>
            )}
          </div>
        </div>
      </div>
    );
  };

  const GuideModal = () => (
    <div
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(0,0,0,0.75)",
        zIndex: 10000,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: "1rem",
      }}
    >
      <div
        style={{
          background: "var(--card)",
          borderRadius: "16px",
          border: "1px solid var(--border)",
          maxWidth: "560px",
          width: "100%",
          overflow: "hidden",
          boxShadow: "0 24px 64px rgba(0,0,0,0.6)",
        }}
      >
        <div
          style={{
            padding: "1.5rem",
            background: "rgba(59,130,246,0.08)",
            borderBottom: "1px solid var(--border)",
          }}
        >
          <div
            style={{
              fontSize: "1.25rem",
              fontWeight: 700,
              marginBottom: "4px",
            }}
          >
            👋 Как пользоваться редактором
          </div>
          <div style={{ color: "#9ca3af", fontSize: "0.85rem" }}>
            Быстрый гайд за 30 секунд
          </div>
        </div>

        <div style={{ padding: "1.5rem" }}>
          {[
            {
              step: "1",
              icon: "📝",
              title: "Настройте шаблон",
              desc: "Укажите название, страну и тип документа в левой панели.",
            },
            {
              step: "2",
              icon: "✏️",
              title: "Редактируйте документ",
              desc: "Пишите текст прямо в редакторе. Для вставки данных используйте переменные — переменная выглядит как {{ имя_поля }}.",
            },
            {
              step: "3",
              icon: "🔤",
              title: "Вставляйте переменные",
              desc: "Кликните на любую переменную из списка слева — она скопируется. Затем щёлкните в нужное место документа и нажмите Ctrl+V.",
            },
            {
              step: "4",
              icon: "💾",
              title: "Сохраните шаблон",
              desc: "Нажмите кнопку «Сохранить шаблон». Система проверит все переменные на корректность.",
            },
          ].map((item) => (
            <div
              key={item.step}
              style={{
                display: "flex",
                gap: "14px",
                marginBottom: "1.1rem",
                alignItems: "flex-start",
              }}
            >
              <div
                style={{
                  width: "36px",
                  height: "36px",
                  borderRadius: "50%",
                  background: "rgba(59,130,246,0.15)",
                  border: "1px solid rgba(59,130,246,0.3)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontSize: "1.1rem",
                  flexShrink: 0,
                }}
              >
                {item.icon}
              </div>
              <div>
                <div
                  style={{
                    fontWeight: 600,
                    fontSize: "0.9rem",
                    marginBottom: "2px",
                  }}
                >
                  {item.title}
                </div>
                <div
                  style={{
                    fontSize: "0.8rem",
                    color: "#9ca3af",
                    lineHeight: 1.5,
                  }}
                >
                  {item.desc}
                </div>
              </div>
            </div>
          ))}

          <div
            style={{
              padding: "12px 16px",
              background: "rgba(16,185,129,0.08)",
              border: "1px solid rgba(16,185,129,0.2)",
              borderRadius: "8px",
              fontSize: "0.8rem",
              color: "#6ee7b7",
              marginTop: "0.5rem",
            }}
          >
            💡 <strong>Пример переменной:</strong>{" "}
            <code
              style={{
                background: "rgba(0,0,0,0.2)",
                padding: "2px 6px",
                borderRadius: "4px",
              }}
            >
              {"{{ surname }}"}
            </code>{" "}
            — будет заменено на фамилию из документа при генерации.
          </div>
        </div>

        <div
          style={{
            padding: "1rem 1.5rem",
            borderTop: "1px solid var(--border)",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <label
            style={{
              display: "flex",
              alignItems: "center",
              gap: "8px",
              fontSize: "0.8rem",
              color: "#9ca3af",
              cursor: "pointer",
            }}
          >
            <input
              type="checkbox"
              onChange={(e) => {
                if (e.target.checked) {
                  localStorage.setItem("templateEditorGuideShown", "true");
                }
              }}
            />
            Больше не показывать
          </label>
          <button
            onClick={() => setShowGuide(false)}
            className="primary-btn"
            style={{ padding: "8px 24px" }}
          >
            Понятно, начать!
          </button>
        </div>
      </div>
    </div>
  );

  const LoadFromTemplatesPanel = () => {
    const filtered = existingTemplates.filter(
      (t) =>
        t.label.toLowerCase().includes(templateSearch.toLowerCase()) ||
        (t.country_code || "")
          .toLowerCase()
          .includes(templateSearch.toLowerCase()),
    );
    const countryName = (code) =>
      countries.find((c) => c.code === code)?.name || code;

    return (
      <div
        style={{
          position: "absolute",
          top: "calc(100% + 8px)",
          left: "-100px",
          right: 0,
          background: "var(--card)",
          border: "1px solid var(--border)",
          borderRadius: "10px",
          zIndex: 1000,
          boxShadow: "0 8px 32px rgba(0,0,0,0.4)",
          overflow: "hidden",
          minWidth: "260px",
        }}
      >
        <div
          style={{
            padding: "10px",
            borderBottom: "1px solid var(--border)",
            background: "rgba(59,130,246,0.05)",
          }}
        >
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: "8px",
              padding: "8px 12px",
              background: "rgba(255,255,255,0.05)",
              borderRadius: "6px",
              border: "1px solid rgba(255,255,255,0.1)",
            }}
          >
            <Search size={14} color="#9ca3af" />
            <input
              type="text"
              placeholder="Поиск шаблона..."
              value={templateSearch}
              onChange={(e) => setTemplateSearch(e.target.value)}
              autoFocus
              style={{
                flex: 1,
                background: "transparent",
                border: "none",
                color: "white",
                fontSize: "0.85rem",
                outline: "none",
              }}
            />
          </div>
        </div>

        <div style={{ maxHeight: "300px", overflowY: "auto" }}>
          {loadingTemplates ? (
            <div
              style={{
                padding: "20px",
                textAlign: "center",
                color: "#9ca3af",
              }}
            >
              <RefreshCw
                size={16}
                style={{ display: "inline", marginRight: "6px" }}
              />
              Загрузка...
            </div>
          ) : filtered.length === 0 ? (
            <div
              style={{
                padding: "20px",
                textAlign: "center",
                color: "#9ca3af",
              }}
            >
              Шаблоны не найдены
            </div>
          ) : (
            filtered.map((t) => (
              <div
                key={t.id}
                onClick={() => handleLoadFromTemplate(t)}
                style={{
                  padding: "10px 14px",
                  cursor: "pointer",
                  borderBottom: "1px solid rgba(255,255,255,0.05)",
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  transition: "background 0.15s",
                }}
                onMouseEnter={(e) =>
                  (e.currentTarget.style.background = "rgba(59,130,246,0.1)")
                }
                onMouseLeave={(e) =>
                  (e.currentTarget.style.background = "transparent")
                }
              >
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div
                    style={{
                      fontSize: "0.9rem",
                      fontWeight: 500,
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {t.own ? "👤" : "🌐"} {t.label}
                    {editingTemplateId === t.id && (
                      <span
                        style={{
                          marginLeft: "8px",
                          fontSize: "0.7rem",
                          color: "#3b82f6",
                          background: "rgba(59,130,246,0.15)",
                          padding: "2px 6px",
                          borderRadius: "4px",
                        }}
                      >
                        Текущий
                      </span>
                    )}
                  </div>
                  <div
                    style={{
                      fontSize: "0.75rem",
                      color: "#9ca3af",
                      marginTop: "2px",
                    }}
                  >
                    {countryName(t.country_code)} ·{" "}
                    {t.date_format || "DD.MM.YYYY"}
                  </div>
                </div>
                <FileText
                  size={16}
                  style={{
                    color: "#9ca3af",
                    flexShrink: 0,
                    marginLeft: "8px",
                  }}
                />
              </div>
            ))
          )}
        </div>
      </div>
    );
  };

  return (
    <div
      style={{
        minHeight: "100vh",
        background: "var(--background)",
        paddingBottom: "2rem",
      }}
    >
      {showGuide && <GuideModal />}

      {showValidationModal && <ValidationModal />}

      <div
        style={{
          background: "var(--card)",
          borderBottom: "1px solid var(--border)",
          padding: "0.75rem 2rem",
          position: "sticky",
          top: 0,
          zIndex: 100,
          boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
        }}
      >
        <div
          style={{
            maxWidth: "1800px",
            margin: "0 auto",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            gap: "1rem",
          }}
        >
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: "1rem",
              flexShrink: 0,
            }}
          >
            <button
              onClick={() => navigate("/templates")}
              className="secondary-btn"
              style={{
                display: "flex",
                alignItems: "center",
                gap: "8px",
                padding: "6px 12px",
              }}
            >
              <ChevronLeft size={18} />
              Назад
            </button>
            <h2 style={{ margin: 0, fontSize: "1.25rem" }}>
              Редактор шаблонов
            </h2>
          </div>

          <div
            style={{
              display: "flex",
              gap: "8px",
              alignItems: "center",
              overflowX: "auto",
            }}
          >
            <StepBadge
              number="1"
              label="Редактор загружен"
              done={step1Done}
              active={!step1Done}
            />
            <span style={{ color: "#374151", flexShrink: 0 }}>→</span>
            <StepBadge
              number="2"
              label="Вставьте переменные"
              done={false}
              active={step1Done}
            />
            <span style={{ color: "#374151", flexShrink: 0 }}>→</span>
            <StepBadge
              number="3"
              label="Укажите название"
              done={step3Done}
              active={step1Done && !step3Done}
            />
            <span style={{ color: "#374151", flexShrink: 0 }}>→</span>
            <StepBadge
              number="4"
              label="Сохраните"
              done={false}
              active={step1Done && step3Done}
            />
          </div>

          <Tooltip text="Открыть инструкцию">
            <button
              onClick={() => setShowGuide(true)}
              className="secondary-btn"
              style={{
                display: "flex",
                alignItems: "center",
                gap: "6px",
                padding: "6px 12px",
                fontSize: "0.85rem",
                flexShrink: 0,
              }}
            >
              <Info size={16} />
              Как пользоваться
            </button>
          </Tooltip>
        </div>
      </div>

      <div
        style={{ maxWidth: "1800px", margin: "0 auto", padding: "1rem 2rem" }}
      >
        <div style={{ display: "flex", gap: "2rem", alignItems: "flex-start" }}>
          <div
            style={{
              width: "360px",
              flexShrink: 0,
              position: "sticky",
              top: "80px",
              maxHeight: "calc(100vh - 100px)",
              overflowY: "auto",
              display: "flex",
              flexDirection: "column",
              gap: "1rem",
              paddingRight: "8px",
            }}
          >
            <div
              style={{
                background: "var(--card)",
                borderRadius: "12px",
                border: "1px solid var(--border)",
              }}
            >
              <div
                style={{
                  padding: "0.875rem 1rem",
                  borderBottom: "1px solid var(--border)",
                  background: "rgba(59, 130, 246, 0.05)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                }}
              >
                <h3 style={{ margin: 0, fontSize: "0.95rem" }}>
                  ⚙️ Настройки шаблона
                </h3>
                {editingTemplateId && (
                  <span
                    style={{
                      fontSize: "0.7rem",
                      color: "#3b82f6",
                      background: "rgba(59,130,246,0.15)",
                      padding: "2px 8px",
                      borderRadius: "10px",
                      border: "1px solid rgba(59,130,246,0.3)",
                    }}
                  >
                    ✏️ Режим редактирования
                  </span>
                )}
              </div>

              <div
                style={{
                  padding: "1rem",
                  display: "flex",
                  flexDirection: "column",
                  gap: "0.875rem",
                }}
              >
                <div className="editable-field">
                  <span className="editable-field-label">
                    Название шаблона <span style={{ color: "#ef4444" }}>*</span>
                  </span>
                  <input
                    className="editable-input"
                    type="text"
                    placeholder="Например: Паспорт Таджикистан 2024"
                    value={label}
                    onChange={(e) => setLabel(e.target.value)}
                    style={{
                      width: "100%",
                      borderColor:
                        label.trim() === "" ? "rgba(239,68,68,0.4)" : undefined,
                    }}
                  />
                  {label.trim() === "" && (
                    <div
                      style={{
                        fontSize: "0.72rem",
                        color: "#f87171",
                        marginTop: "3px",
                      }}
                    >
                      Обязательное поле
                    </div>
                  )}
                </div>

                <div className="editable-field">
                  <span className="editable-field-label">Страна документа</span>
                  <select
                    className="editable-input"
                    value={countryCode}
                    onChange={(e) => setCountryCode(e.target.value)}
                    style={{ width: "100%" }}
                  >
                    {countries.map((c) => (
                      <option key={c.code} value={c.code}>
                        {c.name}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="editable-field">
                  <span className="editable-field-label">Формат дат</span>
                  <select
                    className="editable-input"
                    value={dateFormat}
                    onChange={(e) => setDateFormat(e.target.value)}
                    style={{ width: "100%" }}
                  >
                    <option value="DD.MM.YYYY">31.12.2000 (DD.MM.YYYY)</option>
                    <option value="DD/MM/YYYY">31/12/2000 (DD/MM/YYYY)</option>
                  </select>
                </div>

                <div className="editable-field">
                  <span className="editable-field-label">Тип документа</span>
                  <select
                    className="editable-input"
                    value={documentType}
                    onChange={(e) => setDocumentType(e.target.value)}
                    style={{ width: "100%" }}
                  >
                    <option value="passport">🛂 Паспорт</option>
                    <option value="drivers_license">
                      🚗 Водительское удостоверение
                    </option>
                    {CIVIL_DOC_KINDS.map((k) => (
                      <option key={k.key} value={k.key}>
                        🏛 {k.label}
                      </option>
                    ))}
                  </select>
                </div>

                <div
                  style={{
                    borderTop: "1px solid var(--border)",
                    paddingTop: "0.875rem",
                    display: "flex",
                    flexDirection: "column",
                    gap: "0.5rem",
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      gap: "0.5rem",
                      position: "relative",
                    }}
                  >
                    <Tooltip text="Загрузить готовый .docx файл">
                      <label
                        className="secondary-btn"
                        style={{
                          flex: 1,
                          cursor: "pointer",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          gap: "6px",
                          padding: "8px 10px",
                          fontSize: "0.82rem",
                        }}
                      >
                        <Upload size={15} />
                        Загрузить .docx
                        <input
                          type="file"
                          accept=".doc,.docx,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
                          onChange={handleLoadDocx}
                          style={{ display: "none" }}
                        />
                      </label>
                    </Tooltip>

                    <div style={{ position: "relative" }}>
                      <Tooltip text="Загрузить существующий шаблон">
                        <button
                          onClick={() => {
                            if (!showLoadFromTemplates)
                              fetchExistingTemplates();
                            setShowLoadFromTemplates(!showLoadFromTemplates);
                          }}
                          className="secondary-btn"
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: "6px",
                            padding: "8px 10px",
                            fontSize: "0.82rem",
                            whiteSpace: "nowrap",
                            borderColor: showLoadFromTemplates
                              ? "var(--primary)"
                              : undefined,
                          }}
                        >
                          <FileText size={15} />
                          Из шаблонов
                        </button>
                      </Tooltip>
                      {showLoadFromTemplates && <LoadFromTemplatesPanel />}
                    </div>
                  </div>

                  {showLoadFromTemplates && (
                    <div
                      style={{ position: "fixed", inset: 0, zIndex: 999 }}
                      onClick={() => setShowLoadFromTemplates(false)}
                    />
                  )}

                  <Tooltip
                    text={
                      !isDocumentReady
                        ? "Дождитесь загрузки редактора"
                        : "Скачать текущую версию документа"
                    }
                  >
                    <button
                      onClick={handleDownloadDocx}
                      className="secondary-btn"
                      disabled={!isDocumentReady}
                      style={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        gap: "8px",
                        padding: "8px 12px",
                        fontSize: "0.85rem",
                        width: "100%",
                        opacity: !isDocumentReady ? 0.5 : 1,
                      }}
                    >
                      <Download size={15} />
                      Скачать .docx
                    </button>
                  </Tooltip>

                  <button
                    onClick={handleSaveAsTemplate}
                    className="primary-btn"
                    disabled={
                      saving ||
                      !label.trim() ||
                      !isDocumentReady ||
                      (isValidating && !useVisualMapping)
                    }
                    style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      gap: "8px",
                      padding: "10px 12px",
                      fontSize: "0.95rem",
                      fontWeight: 600,
                      position: "relative",
                    }}
                  >
                    {isValidating && !useVisualMapping ? (
                      <>
                        <RefreshCw
                          size={16}
                          style={{
                            animation: "spin 1s linear infinite",
                          }}
                        />
                        Проверка переменных...
                      </>
                    ) : saving ? (
                      <>
                        <RefreshCw
                          size={16}
                          style={{ animation: "spin 1s linear infinite" }}
                        />
                        Сохранение...
                      </>
                    ) : (
                      <>
                        <Save size={16} />
                        {editingTemplateId
                          ? "Обновить шаблон"
                          : "Сохранить шаблон"}
                      </>
                    )}
                  </button>

                  {!label.trim() && isDocumentReady && (
                    <div
                      style={{
                        fontSize: "0.75rem",
                        color: "#f87171",
                        textAlign: "center",
                      }}
                    >
                      ↑ Введите название, чтобы сохранить
                    </div>
                  )}

                  {editingTemplateId && (
                    <button
                      onClick={() => {
                        setEditingTemplateId(null);
                        createEmptyDocument();
                        setLabel("");
                        setVisualMappingRows([]);
                        setUseVisualMapping(true);
                      }}
                      style={{
                        background: "transparent",
                        border: "1px dashed rgba(255,255,255,0.15)",
                        color: "#9ca3af",
                        cursor: "pointer",
                        fontSize: "0.8rem",
                        textAlign: "center",
                        padding: "6px",
                        borderRadius: "6px",
                        transition: "all 0.2s",
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.borderColor =
                          "rgba(255,255,255,0.3)";
                        e.currentTarget.style.color = "white";
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.borderColor =
                          "rgba(255,255,255,0.15)";
                        e.currentTarget.style.color = "#9ca3af";
                      }}
                    >
                      <Plus size={13} style={{ display: "inline" }} /> Создать
                      новый шаблон
                    </button>
                  )}
                </div>

                {!isDocumentReady && (
                  <div
                    style={{
                      padding: "0.75rem",
                      background: "rgba(251, 191, 36, 0.1)",
                      border: "1px solid rgba(251, 191, 36, 0.3)",
                      borderRadius: "6px",
                      fontSize: "0.82rem",
                      color: "#f59e0b",
                      textAlign: "center",
                    }}
                  >
                    ⏳ Загрузка редактора — подождите...
                  </div>
                )}
              </div>
            </div>

            <div
              style={{
                background: "var(--card)",
                borderRadius: "12px",
                border: "1px solid var(--border)",
                marginBottom: "0.75rem",
              }}
            >
              <div style={{ padding: "0.875rem 1rem" }}>
                <h3 style={{ margin: "0 0 0.75rem", fontSize: "0.95rem" }}>
                  Подстановки в документ
                </h3>
                <div style={{ display: "flex", gap: "8px", flexWrap: "wrap" }}>
                  <button
                    type="button"
                    onClick={() => setUseVisualMapping(true)}
                    className={useVisualMapping ? "primary-btn" : "secondary-btn"}
                    style={{
                      flex: "1 1 140px",
                      padding: "8px 10px",
                      fontSize: "0.82rem",
                    }}
                  >
                    Разметка списком
                  </button>
                  <button
                    type="button"
                    onClick={() => setUseVisualMapping(false)}
                    className={!useVisualMapping ? "primary-btn" : "secondary-btn"}
                    style={{
                      flex: "1 1 140px",
                      padding: "8px 10px",
                      fontSize: "0.82rem",
                    }}
                  >
                    Вручную {"{{ }}"} в Word
                  </button>
                </div>
                {useVisualMapping ? (
                  <div style={{ marginTop: "0.875rem" }}>
                    <p
                      style={{
                        fontSize: "0.78rem",
                        color: "#9ca3af",
                        lineHeight: 1.5,
                        margin: "0 0 0.75rem",
                      }}
                    >
                      Укажите точный текст из вашего .docx (как в редакторе справа)
                      и выберите поле. При генерации этот фрагмент будет заменён на
                      данные из паспорта. Длинные фрагменты указывайте раньше коротких,
                      если одно вхождение входит в другое.
                    </p>
                    <div style={{ display: "flex", gap: "8px", flexWrap: "wrap" }}>
                      <button
                        type="button"
                        onClick={addVisualRow}
                        className="secondary-btn"
                        style={{
                          padding: "6px 12px",
                          fontSize: "0.8rem",
                          display: "flex",
                          alignItems: "center",
                          gap: "6px",
                        }}
                      >
                        <Plus size={14} /> Добавить строку
                      </button>
                      <button
                        type="button"
                        onClick={fetchDocParagraphs}
                        disabled={!documentUrl || paragraphsLoading}
                        className="secondary-btn"
                        style={{
                          padding: "6px 12px",
                          fontSize: "0.8rem",
                          opacity: !documentUrl ? 0.5 : 1,
                        }}
                      >
                        {paragraphsLoading
                          ? "Загрузка абзацев…"
                          : "Показать абзацы документа"}
                      </button>
                    </div>
                    {paragraphsOpen && docParagraphs.length > 0 && (
                      <div style={{ marginTop: "0.75rem" }}>
                        <label
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: "8px",
                            fontSize: "0.8rem",
                            color: "#e2e8f0",
                            cursor: "pointer",
                            marginBottom: "8px",
                            userSelect: "none",
                          }}
                        >
                          <input
                            type="checkbox"
                            checked={hideLongParagraphs}
                            onChange={(e) =>
                              setHideLongParagraphs(e.target.checked)
                            }
                          />
                          Скрыть длинные абзацы (типовой текст паспорта, свыше 120
                          символов)
                        </label>
                        {visibleDocParagraphs.length === 0 && hideLongParagraphs ? (
                          <div
                            style={{
                              fontSize: "0.8rem",
                              color: "#94a3b8",
                              padding: "10px",
                              background: "rgba(15,23,42,0.6)",
                              borderRadius: "8px",
                              border: "1px solid rgba(148,163,184,0.35)",
                            }}
                          >
                            Все абзацы длинные — снимите галочку выше, чтобы показать
                            весь список.
                          </div>
                        ) : null}
                        <div
                          style={{
                            maxHeight: "min(320px, 45vh)",
                            overflowY: "auto",
                            border: "1px solid rgba(148,163,184,0.4)",
                            borderRadius: "8px",
                            background: "rgba(15,23,42,0.92)",
                            boxShadow: "inset 0 0 0 1px rgba(255,255,255,0.06)",
                          }}
                        >
                          {visibleDocParagraphs.map((p) => {
                            const short =
                              (p.text || "").length > 100
                                ? `${(p.text || "").slice(0, 100).trim()}…`
                                : p.text || "";
                            return (
                              <button
                                key={p.index}
                                type="button"
                                title={(p.text || "").length > 100 ? p.text : undefined}
                                onClick={() => {
                                  const id = `vm_${Date.now()}_${Math.random().toString(36).slice(2, 9)}`;
                                  setVisualMappingRows((prev) => [
                                    ...prev,
                                    {
                                      id,
                                      template_key: "",
                                      literal: p.text,
                                    },
                                  ]);
                                }}
                                style={{
                                  display: "block",
                                  width: "100%",
                                  textAlign: "left",
                                  padding: "10px 12px",
                                  fontSize: "0.84rem",
                                  lineHeight: 1.45,
                                  color: "#f8fafc",
                                  background: "transparent",
                                  border: "none",
                                  borderBottom:
                                    "1px solid rgba(148,163,184,0.15)",
                                  cursor: "pointer",
                                }}
                                onMouseEnter={(e) => {
                                  e.currentTarget.style.background =
                                    "rgba(59,130,246,0.2)";
                                }}
                                onMouseLeave={(e) => {
                                  e.currentTarget.style.background = "transparent";
                                }}
                              >
                                <span
                                  style={{
                                    color: "#38bdf8",
                                    marginRight: "8px",
                                    fontWeight: 700,
                                    fontVariantNumeric: "tabular-nums",
                                  }}
                                >
                                  {p.index + 1}.
                                </span>
                                {short}
                              </button>
                            );
                          })}
                        </div>
                        <p
                          style={{
                            fontSize: "0.72rem",
                            color: "#94a3b8",
                            margin: "6px 0 0",
                            lineHeight: 1.4,
                          }}
                        >
                          Клик по строке подставляет в разметку{" "}
                          <strong style={{ color: "#e2e8f0" }}>весь</strong> текст
                          абзаца. Для длинных строк смотрите полный текст во всплывающей
                          подсказке (наведите курсор).
                        </p>
                      </div>
                    )}
                    <div
                      style={{
                        display: "flex",
                        flexDirection: "column",
                        gap: "10px",
                        marginTop: "0.75rem",
                      }}
                    >
                      {visualMappingRows.length === 0 && (
                        <div
                          style={{
                            fontSize: "0.78rem",
                            color: "#64748b",
                            fontStyle: "italic",
                          }}
                        >
                          Нет строк. Нажмите «Добавить строку» или выберите абзац
                          выше.
                        </div>
                      )}
                      {visualMappingRows.map((row) => (
                        <div
                          key={row.id}
                          style={{
                            display: "flex",
                            flexDirection: "column",
                            gap: "6px",
                            padding: "10px",
                            background: "rgba(255,255,255,0.03)",
                            borderRadius: "8px",
                            border: "1px solid rgba(255,255,255,0.08)",
                          }}
                        >
                          <div
                            style={{
                              display: "flex",
                              justifyContent: "space-between",
                              alignItems: "center",
                            }}
                          >
                            <span
                              style={{
                                fontSize: "0.72rem",
                                color: "#94a3b8",
                                textTransform: "uppercase",
                              }}
                            >
                              Поле данных
                            </span>
                            <button
                              type="button"
                              onClick={() => removeVisualRow(row.id)}
                              style={{
                                background: "none",
                                border: "none",
                                color: "#94a3b8",
                                cursor: "pointer",
                                padding: "2px",
                              }}
                              title="Удалить"
                            >
                              <X size={16} />
                            </button>
                          </div>
                          <select
                            className="editable-input"
                            value={row.template_key}
                            onChange={(e) =>
                              updateVisualRow(row.id, {
                                template_key: e.target.value,
                              })
                            }
                            style={{ width: "100%", fontSize: "0.82rem" }}
                          >
                            <option value="">— выберите поле —</option>
                            {documentType === "passport" &&
                              passportPageVarKeys.map((pk) => (
                              <option key={pk} value={pk}>
                                {pk} (страница)
                              </option>
                            ))}
                            {flatFieldSelectOptions.map((o) => (
                              <option key={o.key} value={o.key}>
                                {o.label} ({o.key})
                              </option>
                            ))}
                          </select>
                          <span
                            style={{
                              fontSize: "0.72rem",
                              color: "#94a3b8",
                              textTransform: "uppercase",
                            }}
                          >
                            Точный текст в документе
                          </span>
                          <textarea
                            className="editable-input"
                            value={row.literal}
                            onChange={(e) =>
                              updateVisualRow(row.id, {
                                literal: e.target.value,
                              })
                            }
                            rows={2}
                            placeholder="Например: ИВАНОВ или та же строка, что в Word"
                            style={{
                              width: "100%",
                              fontSize: "0.82rem",
                              resize: "vertical",
                            }}
                          />
                        </div>
                      ))}
                    </div>
                  </div>
                ) : (
                  <p
                    style={{
                      fontSize: "0.78rem",
                      color: "#9ca3af",
                      margin: "0.75rem 0 0",
                      lineHeight: 1.5,
                    }}
                  >
                    Используйте список «Переменные» ниже: вставьте{" "}
                    <code style={{ color: "#93c5fd" }}>{"{{ поле }}"}</code> в
                    документ вручную.
                  </p>
                )}
              </div>
            </div>

            <div
              style={{
                background: "var(--card)",
                borderRadius: "12px",
                border: "1px solid var(--border)",
              }}
            >
              <div
                style={{
                  padding: "0.875rem 1rem",
                  borderBottom: "1px solid var(--border)",
                  background: "rgba(59, 130, 246, 0.05)",
                }}
              >
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "10px",
                    marginBottom: "0.75rem",
                  }}
                >
                  <Code size={16} color="#3b82f6" />
                  <h3 style={{ margin: 0, fontSize: "0.95rem" }}>Переменные</h3>
                  <Tooltip text="Переменные — это поля, которые заполняются автоматически из данных документа при генерации">
                    <Info
                      size={14}
                      color="#9ca3af"
                      style={{ cursor: "help" }}
                    />
                  </Tooltip>
                </div>

                {!useVisualMapping && (
                  <div
                    style={{
                      padding: "8px 10px",
                      background: "rgba(59,130,246,0.08)",
                      border: "1px solid rgba(59,130,246,0.2)",
                      borderRadius: "6px",
                      fontSize: "0.75rem",
                      color: "#93c5fd",
                      marginBottom: "0.75rem",
                      lineHeight: 1.5,
                    }}
                  >
                    👆 Кликните на переменную → она скопируется → нажмите{" "}
                    <kbd
                      style={{
                        background: "rgba(0,0,0,0.3)",
                        padding: "1px 5px",
                        borderRadius: "3px",
                        fontFamily: "monospace",
                        fontSize: "0.7rem",
                      }}
                    >
                      Ctrl+V
                    </kbd>{" "}
                    в документе
                  </div>
                )}
                {useVisualMapping && (
                  <div
                    style={{
                      padding: "8px 10px",
                      background: "rgba(16,185,129,0.08)",
                      border: "1px solid rgba(16,185,129,0.25)",
                      borderRadius: "6px",
                      fontSize: "0.75rem",
                      color: "#6ee7b7",
                      marginBottom: "0.75rem",
                      lineHeight: 1.5,
                    }}
                  >
                    Режим списка: подстановки задаются в блоке «Подстановки в
                    документ» выше. Список ниже — для справки по именам полей.
                  </div>
                )}

                <select
                  className="editable-input"
                  value={selectedCountryForVars}
                  onChange={(e) => setSelectedCountryForVars(e.target.value)}
                  style={{
                    width: "100%",
                    fontSize: "0.82rem",
                    marginBottom: "8px",
                  }}
                >
                  <option value="">Все страны</option>
                  {countries.map((c) => (
                    <option key={c.code} value={c.code}>
                      {c.name}
                    </option>
                  ))}
                </select>

                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "8px",
                    padding: "6px 10px",
                    background: "rgba(255,255,255,0.04)",
                    borderRadius: "6px",
                    border: "1px solid rgba(255,255,255,0.08)",
                  }}
                >
                  <Search size={13} color="#9ca3af" />
                  <input
                    type="text"
                    placeholder="Поиск переменной..."
                    value={varSearch}
                    onChange={(e) => setVarSearch(e.target.value)}
                    style={{
                      flex: 1,
                      background: "transparent",
                      border: "none",
                      color: "white",
                      fontSize: "0.8rem",
                      outline: "none",
                    }}
                  />
                  {varSearch && (
                    <button
                      onClick={() => setVarSearch("")}
                      style={{
                        background: "transparent",
                        border: "none",
                        color: "#9ca3af",
                        cursor: "pointer",
                        padding: "0",
                        display: "flex",
                      }}
                    >
                      <X size={13} />
                    </button>
                  )}
                </div>
              </div>

              <div style={{ padding: "1rem" }}>
                {filteredVarsWithSearch.length === 0 && varSearch && (
                  <div
                    style={{
                      textAlign: "center",
                      color: "#9ca3af",
                      fontSize: "0.82rem",
                      padding: "1rem 0",
                    }}
                  >
                    Переменные не найдены
                  </div>
                )}

                {filteredVarsWithSearch.map(([categoryKey, category]) => (
                  <div key={categoryKey} style={{ marginBottom: "1.5rem" }}>
                    <h4
                      style={{
                        fontSize: "0.75rem",
                        textTransform: "uppercase",
                        color: "#3b82f6",
                        marginBottom: "0.6rem",
                        fontWeight: 700,
                        letterSpacing: "0.5px",
                        display: "flex",
                        alignItems: "center",
                        gap: "6px",
                      }}
                    >
                      {category.name}
                      <span
                        style={{
                          background: "rgba(59,130,246,0.15)",
                          color: "#60a5fa",
                          padding: "1px 6px",
                          borderRadius: "8px",
                          fontSize: "0.65rem",
                          fontWeight: 600,
                        }}
                      >
                        {category.fields.length}
                      </span>
                    </h4>

                    <div
                      style={{
                        display: "flex",
                        flexDirection: "column",
                        gap: "4px",
                      }}
                    >
                      {category.fields.map((field) => (
                        <div key={field.key}>
                          <div
                            onClick={() =>
                              canManualPaste &&
                              handleInsertVariable(field.key)
                            }
                            title={
                              useVisualMapping
                                ? "Отключите «Разметка списком» для вставки {{ }} в Word"
                                : isDocumentReady
                                  ? `Кликните, чтобы скопировать {{ ${field.key} }}`
                                  : "Дождитесь загрузки редактора"
                            }
                            style={{
                              display: "flex",
                              alignItems: "center",
                              gap: "8px",
                              padding: "7px 10px",
                              background: "rgba(255, 255, 255, 0.03)",
                              border: "1px solid rgba(255, 255, 255, 0.05)",
                              borderRadius: "6px",
                              cursor: canManualPaste
                                ? "pointer"
                                : "not-allowed",
                              transition: "all 0.15s",
                              opacity: canManualPaste ? 1 : 0.5,
                            }}
                            onMouseEnter={(e) => {
                              if (canManualPaste) {
                                e.currentTarget.style.background =
                                  "rgba(59, 130, 246, 0.12)";
                                e.currentTarget.style.borderColor =
                                  "rgba(59, 130, 246, 0.35)";
                              }
                            }}
                            onMouseLeave={(e) => {
                              e.currentTarget.style.background =
                                "rgba(255, 255, 255, 0.03)";
                              e.currentTarget.style.borderColor =
                                "rgba(255, 255, 255, 0.05)";
                            }}
                          >
                            <Plus
                              size={13}
                              color="rgba(255, 255, 255, 0.25)"
                              style={{ flexShrink: 0 }}
                            />
                            <div style={{ flex: 1, minWidth: 0 }}>
                              <code
                                style={{
                                  fontSize: "0.78rem",
                                  color: "#60a5fa",
                                  display: "block",
                                  marginBottom: "1px",
                                  overflow: "hidden",
                                  textOverflow: "ellipsis",
                                  whiteSpace: "nowrap",
                                }}
                              >
                                {field.key}
                              </code>
                              <div
                                style={{
                                  fontSize: "0.68rem",
                                  color: "rgba(255, 255, 255, 0.45)",
                                  overflow: "hidden",
                                  textOverflow: "ellipsis",
                                  whiteSpace: "nowrap",
                                }}
                              >
                                {field.label}
                              </div>
                            </div>
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                handleCopyVar(field.key);
                              }}
                              title="Копировать без вставки"
                              style={{
                                background: "transparent",
                                border: "none",
                                color:
                                  copiedVar === field.key
                                    ? "#10b981"
                                    : "rgba(255, 255, 255, 0.3)",
                                cursor: "pointer",
                                padding: "3px",
                                display: "flex",
                                alignItems: "center",
                                flexShrink: 0,
                              }}
                            >
                              {copiedVar === field.key ? (
                                <Check size={13} />
                              ) : (
                                <Copy size={13} />
                              )}
                            </button>
                          </div>

                          {field.has_parts && (
                            <div
                              style={{
                                marginLeft: "1.25rem",
                                marginTop: "3px",
                                paddingLeft: "8px",
                                borderLeft: "2px solid rgba(96, 165, 250, 0.2)",
                              }}
                            >
                              {field.parts.map((part) => (
                                <div
                                  key={part.key}
                                  onClick={() =>
                                    canManualPaste &&
                                    handleInsertVariable(part.key)
                                  }
                                  title={`{{ ${part.key} }}`}
                                  style={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: "6px",
                                    padding: "5px 8px",
                                    background: "rgba(255, 255, 255, 0.02)",
                                    borderRadius: "4px",
                                    cursor: canManualPaste
                                      ? "pointer"
                                      : "not-allowed",
                                    marginBottom: "2px",
                                    opacity: canManualPaste ? 1 : 0.5,
                                    transition: "background 0.15s",
                                  }}
                                  onMouseEnter={(e) => {
                                    if (canManualPaste)
                                      e.currentTarget.style.background =
                                        "rgba(59,130,246,0.1)";
                                  }}
                                  onMouseLeave={(e) => {
                                    e.currentTarget.style.background =
                                      "rgba(255,255,255,0.02)";
                                  }}
                                >
                                  <Plus
                                    size={11}
                                    color="rgba(255, 255, 255, 0.15)"
                                  />
                                  <code
                                    style={{
                                      fontSize: "0.72rem",
                                      color: "#60a5fa",
                                      flex: 1,
                                      overflow: "hidden",
                                      textOverflow: "ellipsis",
                                      whiteSpace: "nowrap",
                                    }}
                                  >
                                    {part.key}
                                  </code>
                                  <button
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleCopyVar(part.key);
                                    }}
                                    style={{
                                      background: "transparent",
                                      border: "none",
                                      color:
                                        copiedVar === part.key
                                          ? "#10b981"
                                          : "rgba(255, 255, 255, 0.25)",
                                      cursor: "pointer",
                                      padding: "2px",
                                      display: "flex",
                                    }}
                                  >
                                    {copiedVar === part.key ? (
                                      <Check size={11} />
                                    ) : (
                                      <Copy size={11} />
                                    )}
                                  </button>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                ))}

                {documentType === "passport" && !varSearch && (
                  <div style={{ marginBottom: "1.5rem" }}>
                    <h4
                      style={{
                        fontSize: "0.75rem",
                        textTransform: "uppercase",
                        color: "#3b82f6",
                        marginBottom: "0.6rem",
                        fontWeight: 700,
                        letterSpacing: "0.5px",
                        display: "flex",
                        alignItems: "center",
                        gap: "6px",
                      }}
                    >
                      Страницы паспорта
                      <span
                        style={{
                          background: "rgba(59,130,246,0.15)",
                          color: "#60a5fa",
                          padding: "1px 6px",
                          borderRadius: "8px",
                          fontSize: "0.65rem",
                          fontWeight: 600,
                        }}
                      >
                        8
                      </span>
                    </h4>
                    <div
                      style={{
                        display: "grid",
                        gridTemplateColumns: "1fr 1fr",
                        gap: "4px",
                      }}
                    >
                      {[
                        "03",
                        "04",
                        "05",
                        "06",
                        "07",
                        "08",
                        "09",
                        "10",
                        "11",
                        "12",
                        "13",
                        "14",
                        "15",
                        "16",
                        "17",
                        "18",
                        "19",
                        "20",
                        "21",
                        "22",
                        "23",
                        "24",
                        "25",
                        "26",
                        "27",
                        "28",
                        "29",
                        "30",
                        "31",
                        "32",
                        "33",
                        "34",
                        "35",
                        "36",
                        "37",
                        "38",
                        "39",
                        "40",
                        "41",
                        "42",
                        "43",
                        "44",
                        "45",
                        "46",
                      ].map((num) => {
                        const pageVar = `page${num}`;
                        return (
                          <div
                            key={pageVar}
                            onClick={() =>
                              canManualPaste &&
                              handleInsertVariable(pageVar)
                            }
                            title={`Вставить {{ ${pageVar} }}`}
                            style={{
                              display: "flex",
                              alignItems: "center",
                              gap: "5px",
                              padding: "6px 8px",
                              background: "rgba(255,255,255,0.03)",
                              border: "1px solid rgba(255,255,255,0.05)",
                              borderRadius: "6px",
                              cursor: canManualPaste
                                ? "pointer"
                                : "not-allowed",
                              fontSize: "0.78rem",
                              opacity: canManualPaste ? 1 : 0.5,
                              transition: "all 0.15s",
                            }}
                            onMouseEnter={(e) => {
                              if (canManualPaste) {
                                e.currentTarget.style.background =
                                  "rgba(59,130,246,0.12)";
                                e.currentTarget.style.borderColor =
                                  "rgba(59,130,246,0.35)";
                              }
                            }}
                            onMouseLeave={(e) => {
                              e.currentTarget.style.background =
                                "rgba(255,255,255,0.03)";
                              e.currentTarget.style.borderColor =
                                "rgba(255,255,255,0.05)";
                            }}
                          >
                            <Plus size={11} color="rgba(255,255,255,0.25)" />
                            <code style={{ color: "#60a5fa" }}>{pageVar}</code>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}

                {documentType === "passport" &&
                  varSearch &&
                  [
                    "03",
                    "04",
                    "05",
                    "06",
                    "07",
                    "08",
                    "09",
                    "10",
                    "11",
                    "12",
                    "13",
                    "14",
                    "15",
                    "16",
                    "17",
                    "18",
                    "19",
                    "20",
                    "21",
                    "22",
                    "23",
                    "24",
                    "25",
                    "26",
                    "27",
                    "28",
                    "29",
                    "30",
                    "31",
                    "32",
                    "33",
                    "34",
                    "35",
                    "36",
                    "37",
                    "38",
                    "39",
                    "40",
                    "41",
                    "42",
                    "43",
                    "44",
                    "45",
                    "46",
                  ].some((n) =>
                    `page${n}`.includes(varSearch.toLowerCase()),
                  ) && (
                    <div style={{ marginBottom: "1.5rem" }}>
                      <h4
                        style={{
                          fontSize: "0.75rem",
                          textTransform: "uppercase",
                          color: "#3b82f6",
                          marginBottom: "0.6rem",
                          fontWeight: 700,
                        }}
                      >
                        Страницы паспорта
                      </h4>
                      <div
                        style={{
                          display: "grid",
                          gridTemplateColumns: "1fr 1fr",
                          gap: "4px",
                        }}
                      >
                        {[
                          "03",
                          "04",
                          "05",
                          "06",
                          "07",
                          "08",
                          "09",
                          "10",
                          "11",
                          "12",
                          "13",
                          "14",
                          "15",
                          "16",
                          "17",
                          "18",
                          "19",
                          "20",
                          "21",
                          "22",
                          "23",
                          "24",
                          "25",
                          "26",
                          "27",
                          "28",
                          "29",
                          "30",
                          "31",
                          "32",
                          "33",
                          "34",
                          "35",
                          "36",
                          "37",
                          "38",
                          "39",
                          "40",
                          "41",
                          "42",
                          "43",
                          "44",
                          "45",
                          "46",
                        ]
                          .filter((n) =>
                            `page${n}`.includes(varSearch.toLowerCase()),
                          )
                          .map((num) => {
                            const pageVar = `page${num}`;
                            return (
                              <div
                                key={pageVar}
                                onClick={() =>
                                  canManualPaste &&
                                  handleInsertVariable(pageVar)
                                }
                                style={{
                                  display: "flex",
                                  alignItems: "center",
                                  gap: "5px",
                                  padding: "6px 8px",
                                  background: "rgba(255,255,255,0.03)",
                                  border: "1px solid rgba(255,255,255,0.05)",
                                  borderRadius: "6px",
                                  cursor: canManualPaste
                                    ? "pointer"
                                    : "not-allowed",
                                  fontSize: "0.78rem",
                                  opacity: canManualPaste ? 1 : 0.5,
                                  transition: "all 0.15s",
                                }}
                                onMouseEnter={(e) => {
                                  if (canManualPaste) {
                                    e.currentTarget.style.background =
                                      "rgba(59,130,246,0.12)";
                                    e.currentTarget.style.borderColor =
                                      "rgba(59,130,246,0.35)";
                                  }
                                }}
                                onMouseLeave={(e) => {
                                  e.currentTarget.style.background =
                                    "rgba(255,255,255,0.03)";
                                  e.currentTarget.style.borderColor =
                                    "rgba(255,255,255,0.05)";
                                }}
                              >
                                <Plus
                                  size={11}
                                  color="rgba(255,255,255,0.25)"
                                />
                                <code style={{ color: "#60a5fa" }}>
                                  {pageVar}
                                </code>
                              </div>
                            );
                          })}
                      </div>
                    </div>
                  )}
              </div>
            </div>
          </div>

          <div style={{ flex: 1, minWidth: 0 }}>
            <div
              style={{
                background: "var(--card)",
                borderRadius: "12px",
                border: "1px solid var(--border)",
                overflow: "hidden",
                height: "calc(100vh - 140px)",
              }}
            >
              <div
                style={{
                  padding: "0.875rem 1rem",
                  borderBottom: "1px solid var(--border)",
                  background: "rgba(59, 130, 246, 0.05)",
                }}
              >
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "10px",
                    }}
                  >
                    <FileText size={17} color="#3b82f6" />
                    <h3 style={{ margin: 0, fontSize: "0.95rem" }}>
                      Документ Word
                    </h3>
                    {isDocumentReady && (
                      <span
                        style={{
                          fontSize: "0.7rem",
                          color: "#10b981",
                          background: "rgba(16,185,129,0.12)",
                          padding: "2px 8px",
                          borderRadius: "10px",
                          border: "1px solid rgba(16,185,129,0.25)",
                        }}
                      >
                        ● Готов к редактированию
                      </span>
                    )}
                  </div>

                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "12px",
                    }}
                  >
                    {label && (
                      <span
                        style={{
                          fontSize: "0.82rem",
                          color: "var(--muted-foreground)",
                          fontWeight: 500,
                        }}
                      >
                        {label}
                      </span>
                    )}

                    <div
                      style={{
                        fontSize: "0.75rem",
                        color: "#9ca3af",
                        background: "rgba(255,255,255,0.04)",
                        padding: "4px 10px",
                        borderRadius: "6px",
                        border: "1px solid rgba(255,255,255,0.07)",
                      }}
                    >
                      Переменная:{" "}
                      <code
                        style={{
                          color: "#60a5fa",
                          fontSize: "0.72rem",
                        }}
                      >
                        {"{{ имя_поля }}"}
                      </code>
                    </div>
                  </div>
                </div>
              </div>

              <div style={{ height: "calc(100% - 58px)" }}>
                {config ? (
                  <DocumentEditor
                    id="onlyoffice-editor"
                    documentServerUrl={ONLYOFFICE_SERVER}
                    config={config}
                    onDocumentReady={onDocumentReady}
                  />
                ) : (
                  <div
                    style={{
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      justifyContent: "center",
                      height: "100%",
                      color: "var(--muted-foreground)",
                      gap: "12px",
                    }}
                  >
                    <RefreshCw
                      size={28}
                      style={{
                        animation: "spin 1.2s linear infinite",
                        color: "#3b82f6",
                      }}
                    />
                    <div style={{ fontSize: "0.9rem" }}>
                      Загрузка редактора...
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {notification && (
        <div
          style={{
            position: "fixed",
            bottom: "2rem",
            right: "2rem",
            background: notification.isWaitingPaste
              ? "linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)"
              : "linear-gradient(135deg, #10b981 0%, #059669 100%)",
            color: "white",
            padding: "1.25rem 1.5rem",
            borderRadius: "14px",
            boxShadow: notification.isWaitingPaste
              ? "0 12px 40px rgba(59,130,246,0.5)"
              : "0 12px 40px rgba(16,185,129,0.4)",
            zIndex: 9999,
            maxWidth: "420px",
            animation: "slideIn 0.3s ease",
            border: "2px solid rgba(255,255,255,0.15)",
          }}
        >
          <div
            style={{
              fontWeight: 600,
              fontSize: "0.95rem",
              marginBottom: notification.variable ? "0.5rem" : 0,
            }}
          >
            {notification.text}
          </div>
          {notification.variable && (
            <code
              style={{
                background: "rgba(0,0,0,0.2)",
                padding: "0.4rem 0.8rem",
                borderRadius: "6px",
                display: "block",
                fontSize: "0.9rem",
                fontFamily: "monospace",
              }}
            >
              {notification.variable}
            </code>
          )}
        </div>
      )}

      <style>{`
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
        @keyframes slideIn {
          from { transform: translateY(20px); opacity: 0; }
          to { transform: translateY(0); opacity: 1; }
        }
      `}</style>
    </div>
  );
};

export default TemplateEditor;
